// Bluetooth Classic SPP transport — solo Android (Capacitor nativo).
//
// Sostituisce Web Bluetooth per le stampanti che parlano RFCOMM (UUID 1101),
// come la Clabel CT221B. Tre vantaggi rispetto a BLE:
//   1. Pairing/bonding gestito da Android (BluetoothAdapter.createBond) ⇒
//      l'app Niveo replica esattamente quello che fa l'app del produttore.
//   2. Una sola sessione SPP persistente, condivisa tra tutte le stampe ⇒
//      la stampante non va mai in standby e non si "scollega" dopo un job.
//   3. Heartbeat applicativo + auto-reconnect: se il socket cade, il prossimo
//      write rifa la connect senza errori a livello UI.
//
// API esposta — pensata per essere parallela a ble-transport.ts: l'adapter
// stampante (tspl.ts) sceglie il transport in base alla piattaforma.

import { Capacitor } from '@capacitor/core';
import { BluetoothSerial } from '@e-is/capacitor-bluetooth-serial';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { serializePrinterWrite } from './serializedWriteQueue.js';
import { UserFacingError } from '../../errors.js';

export interface ClassicPrinterDevice {
  address: string;
  name: string;
  rssi: number | null;
  paired: boolean;
}

interface Session {
  address: string;
  heartbeatTimer: ReturnType<typeof setInterval> | null;
}

const sessions = new Map<string, Session>();

function isAvailable(): boolean {
  return Capacitor.getPlatform() === 'android';
}

let blePermissionsRequested = false;

// Il plugin BluetoothSerial fa discovery ATTIVA (BluetoothAdapter.startDiscovery)
// in scan(): su Android 12+ richiede BLUETOOTH_SCAN, ma la scoperta dei
// dispositivi Classic restituisce 0 risultati se ACCESS_FINE_LOCATION non è
// concesso (il manifest dichiara BLUETOOTH_SCAN senza `neverForLocation`,
// quindi il sistema accoppia scan↔posizione). Il plugin però NON chiede mai
// la posizione su Android 12+ (il suo alias per API≥31 è solo SCAN/CONNECT):
// ecco perché l'utente non vedeva il prompt e la ricerca falliva.
//
// Capacitor espone automaticamente requestPermissions() a livello bridge per
// ogni plugin che dichiara @CapacitorPlugin(permissions=…), anche se non è
// nell'interfaccia TS. Lo chiamiamo con ENTRAMBI gli alias del plugin
// ('bluetooth' = COARSE/FINE/BLUETOOTH/BLUETOOTH_ADMIN, 'bluetooth-31' =
// SCAN/CONNECT) così la posizione viene richiesta su OGNI versione Android.
type BridgePerms = {
  requestPermissions?: (o?: { permissions?: string[] }) => Promise<unknown>;
};

async function ensureBtPermissions(): Promise<void> {
  if (blePermissionsRequested) return;
  // androidNeverForLocation:false → coerente col manifest (BLUETOOTH_SCAN
  // senza neverForLocation) e fa sì che anche bluetooth-le pretenda la
  // posizione su Android 12+ invece di sopprimerla.
  try {
    await BleClient.initialize({ androidNeverForLocation: false });
  } catch {
    // Ignora: se fallisce (es. BT già inizializzato) continua comunque.
  }
  try {
    const bridge = BluetoothSerial as unknown as BridgePerms;
    if (typeof bridge.requestPermissions === 'function') {
      await bridge.requestPermissions({ permissions: ['bluetooth', 'bluetooth-31'] });
    }
  } catch {
    // L'utente può negare: lo scan successivo fallirà con messaggio chiaro.
  }
  blePermissionsRequested = true;
}

async function ensureBluetoothEnabled(): Promise<void> {
  // Chiede permessi (incl. POSIZIONE) prima di toccare il Bluetooth.
  await ensureBtPermissions();

  const state = await BluetoothSerial.isEnabled();
  if (!state.enabled) {
    // Android 13+ depreca l'abilitazione programmatica: proviamo ma non ci
    // affidiamo al risultato.
    try { await BluetoothSerial.enable(); } catch { /* ignore */ }
    const retry = await BluetoothSerial.isEnabled();
    if (!retry.enabled) {
      throw new UserFacingError('Bluetooth disabilitato. Abilitalo dalle impostazioni Android.');
    }
  }
}

/** Lista i dispositivi già accoppiati a livello OS (bonded). */
export async function classicListPaired(): Promise<ClassicPrinterDevice[]> {
  if (!isAvailable()) return [];
  await ensureBluetoothEnabled();
  try {
    const result = await BluetoothSerial.scan();
    return result.devices.map((d) => ({
      address: d.address,
      name: d.name || d.address,
      rssi: typeof d.rssi === 'number' && d.rssi !== 0 ? d.rssi : null,
      paired: true,
    }));
  } catch (e) {
    // La discovery BT Classic richiede il permesso Posizione concesso E i
    // Servizi di Localizzazione (GPS) attivi: senza, Android non restituisce
    // dispositivi.
    throw new Error(
      'Ricerca fallita. Concedi il permesso Posizione a TrackEAT e attiva i Servizi di Localizzazione nelle impostazioni Android, poi riprova.',
    );
  }
}

/**
 * Discovery attiva (~12s): trova dispositivi non ancora accoppiati.
 * Usata solo se l'utente vuole aggiungere una nuova stampante non in lista.
 */
export async function classicDiscover(): Promise<ClassicPrinterDevice[]> {
  if (!isAvailable()) return [];
  await ensureBluetoothEnabled();
  const result = await BluetoothSerial.scan();
  return result.devices.map((d) => ({
    address: d.address,
    name: d.name || d.address,
    rssi: typeof d.rssi === 'number' && d.rssi !== 0 ? d.rssi : null,
    paired: true,
  }));
}

/**
 * Apre la connessione SPP. Se il device non è ancora paired Android lancia
 * il flusso di bonding (system dialog del PIN, se richiesto). La sessione
 * resta aperta finché non chiami classicForceDisconnect.
 */
export async function classicConnect(address: string): Promise<void> {
  return serializePrinterWrite(`classic:${address}`, () => connectClassicSocket(address));
}

function connectClassicSocket(address: string): Promise<void> {
  // The native plugin has one connectCall callback, shared across addresses.
  return serializePrinterWrite('classic:connect', () => classicConnectExclusive(address));
}

function rememberSession(address: string): void {
  const session: Session = { address, heartbeatTimer: null };
  session.heartbeatTimer = setInterval(() => {
    // Heartbeat must never replace a socket while a bitmap is being sent.
    void serializePrinterWrite(`classic:${address}`, async () => {
      if (sessions.get(address) === session) await connectClassicSocket(address);
    }).catch(() => undefined);
  }, 60_000);
  sessions.set(address, session);
}

async function classicConnectExclusive(address: string): Promise<void> {
  if (!isAvailable()) throw new Error('Bluetooth Classic disponibile solo su Android.');
  await ensureBluetoothEnabled();

  if (await classicIsConnected(address)) {
    // Re-adopt the native socket after an OTA reload, without opening another.
    if (!sessions.has(address)) rememberSession(address);
    return;
  }
  await classicForceDisconnect(address);
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await BluetoothSerial.connect({ address });
      rememberSession(address);
      return;
    } catch (error) {
      await classicForceDisconnect(address);
      const message = String((error as { message?: unknown } | null)?.message ?? error);
      if (attempt === 1 || !/failed to connect/i.test(message)) {
        throw Object.assign(new UserFacingError(
          'Impossibile collegarsi alla stampante Bluetooth. Controlla che sia accesa e vicina, '
          + 'che il Bluetooth e i permessi siano attivi e che non sia collegata a un altro telefono, poi riprova.',
        ), { cause: error, stage: 'connect' });
      }
      // Retry only the handshake: no label bytes have been sent at this point.
      await new Promise<void>(resolve => setTimeout(resolve, 400));
    }
  }
}

/** Scrive bytes binari sulla stampante. Riconnette automaticamente se serve. */
export async function classicWriteBytes(address: string, data: Uint8Array): Promise<void> {
  return serializePrinterWrite(`classic:${address}`, () => classicWriteBytesExclusive(address, data));
}

async function classicWriteBytesExclusive(address: string, data: Uint8Array): Promise<void> {
  if (!isAvailable()) throw new Error('Bluetooth Classic disponibile solo su Android.');

  // Ensure connection alive
  let session = sessions.get(address);
  if (!session) {
    await connectClassicSocket(address);
    session = sessions.get(address);
  } else {
    const status = await BluetoothSerial.isConnected({ address });
    if (!status.connected) {
      await connectClassicSocket(address);
    }
  }

  // Il plugin accetta `value: string` e lato nativo lo converte in byte con
  // ISO-8859-1 (patch in patches/@e-is+capacitor-bluetooth-serial: prima era
  // UTF-8 e gonfiava i byte ≥0x80, corrompendo i payload binari come il
  // comando TSPL BITMAP). Costruiamo SEMPRE la stringa con mappatura 1:1
  // byte↔codepoint (latin1): l'ASCII resta identico, il binario passa intatto.
  let value = '';
  for (let i = 0; i < data.length; i += 1) {
    value += String.fromCharCode(data[i]!);
  }

  // Spezzetto in chunk per evitare timeout su buffer grandi (es. bitmap).
  // Il comando TSPL PRINT è in coda al job: se cade un chunk precedente,
  // nessuna etichetta può essere uscita e possiamo riconnettere/riprovare una
  // sola volta. Se fallisce l'ultimo chunk l'esito fisico è invece incerto:
  // NON ristampiamo automaticamente per evitare duplicati.
  const CHUNK = 512;
  const writeJob = async (): Promise<void> => {
    for (let off = 0; off < value.length; off += CHUNK) {
      try {
        await BluetoothSerial.write({
          address,
          value: value.slice(off, off + CHUNK),
        });
      } catch (error) {
        const isFinalChunk = off + CHUNK >= value.length;
        const failure = new Error(
          isFinalChunk
            ? 'bt_write_uncertain'
            : 'bt_write_before_print_failed',
        ) as Error & { cause?: unknown; finalChunk?: boolean };
        failure.cause = error;
        failure.finalChunk = isFinalChunk;
        throw failure;
      }
    }
  };

  try {
    await writeJob();
  } catch (error) {
    const failure = error as Error & { finalChunk?: boolean };
    if (failure.finalChunk) {
      throw new Error(
        'Connessione persa durante il comando di stampa: verifica se '
        + 'l’etichetta è uscita prima di riprovare.',
        { cause: error },
      );
    }
    await classicForceDisconnect(address);
    try {
      await connectClassicSocket(address);
      await writeJob();
    } catch (retryError) {
      const retryFailure = retryError as Error & { finalChunk?: boolean };
      throw new Error(
        retryFailure.finalChunk
          ? 'Connessione persa durante il comando di stampa: verifica se '
            + 'l’etichetta è uscita prima di riprovare.'
          : 'Stampante Bluetooth non raggiungibile dopo la riconnessione. '
            + 'Controlla che sia accesa e collegata, poi riprova.',
        { cause: retryError },
      );
    }
  }
}

/** Restituisce true se il socket SPP è aperto. */
export async function classicIsConnected(address: string): Promise<boolean> {
  if (!isAvailable()) return false;
  try {
    const r = await BluetoothSerial.isConnected({ address });
    return r.connected;
  } catch {
    return false;
  }
}

/** Chiude la sessione per dimenticare la stampante o ripulire un collegamento caduto. */
export async function classicForceDisconnect(address: string): Promise<void> {
  const s = sessions.get(address);
  if (s?.heartbeatTimer) clearInterval(s.heartbeatTimer);
  sessions.delete(address);
  try { await BluetoothSerial.disconnect({ address }); } catch { /* ignore */ }
}
